import type { ReactNode } from "react";

export interface DataColumn<T> {
  readonly header: string;
  readonly key: string;
  readonly render: (item: T) => ReactNode;
}

export function ResponsiveData<T>({
  caption,
  columns,
  getKey,
  items,
  mobileHeading,
}: {
  readonly caption: string;
  readonly columns: readonly DataColumn<T>[];
  readonly getKey: (item: T) => string;
  readonly items: readonly T[];
  readonly mobileHeading: (item: T) => ReactNode;
}) {
  return (
    <>
      <div className="table-wrap">
        <table className="data-table">
          <caption>{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} scope="col">
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={getKey(item)}>
                {columns.map((column) => (
                  <td key={column.key}>{column.render(item)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div aria-label={caption} className="record-list">
        {items.map((item) => (
          <article key={getKey(item)}>
            <h3>{mobileHeading(item)}</h3>
            <dl>
              {columns.map((column) => (
                <div key={column.key}>
                  <dt>{column.header}</dt>
                  <dd>{column.render(item)}</dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>
    </>
  );
}

export function CodeValue({ children }: { readonly children: string }) {
  return (
    <span className="technical">
      {children.replace(
        /[\u001B\u009B][[\]()#;?]*(?:(?:(?:[a-zA-Z\d]*(?:;[-a-zA-Z\d\/#&.:=?%@~_]+)*)?\u0007)|(?:(?:\d{1,4}(?:[;:]\d{0,4})*)?[\dA-PR-TZcf-nq-uy=><~]))/gu,
        "",
      )}
    </span>
  );
}

export function AuditDiff({
  after,
  before,
}: {
  readonly after: Readonly<Record<string, unknown>>;
  readonly before: Readonly<Record<string, unknown>>;
}) {
  const safe = (value: unknown) =>
    typeof value === "string" && /secret|token|password|credential|recovery/iu.test(value)
      ? "•••••••• (gizlendi)"
      : JSON.stringify(value, null, 2);
  return (
    <div className="two-column">
      <section>
        <h3>Önce</h3>
        <pre>{safe(before)}</pre>
      </section>
      <section>
        <h3>Sonra</h3>
        <pre>{safe(after)}</pre>
      </section>
    </div>
  );
}
